import {
  ExtensionsByMachine,
  InstalledExtensionsResponse,
} from '../interfaces/domain/installed-extensions.modal';
import getConfig from 'next/config';
import { ServicesHandler } from './service-handler';
import { getOverwolfCurrentUser } from './overwolf/overwolf-store.service';

const kInstalledExtensionsUrl = 'https://storeapi.overwolf.com/users/apps';

// -----------------------------------------------------------------------------
export class InstalledExtensionsService {
  private static cache: ExtensionsByMachine | null = null;
  private static inFlightRequest: Promise<ExtensionsByMachine | null> | null =
    null;

  // ---------------------------------------------------------------------------
  public static async getExtensionsByMachine(
    forceRefresh = false,
  ): Promise<ExtensionsByMachine | null> {
    const token = await this.resolveToken();
    if (!token) {
      this.cache = null;
      this.inFlightRequest = null;
      return null;
    }

    if (!forceRefresh && this.cache) {
      return this.cache;
    }

    if (this.inFlightRequest) {
      return this.inFlightRequest;
    }

    const request = this.fetchInstalledExtensions(token);
    this.inFlightRequest = request;

    return request;
  }

  // ---------------------------------------------------------------------------
  // Resolve the auth token. Normally it's the `owUser` cookie. Inside the
  // Overwolf app the store runs in a cross-origin iframe with no such cookie, so
  // fall back to the native client's signed-in user over the OverwolfStore
  // bridge — the only way to obtain the token there.
  private static async resolveToken(): Promise<string | null> {
    const { loginService } = ServicesHandler.getInstance(getConfig());
    const cookieToken = loginService.getToken();
    if (cookieToken) {
      return cookieToken;
    }

    if (typeof window === 'undefined') {
      return null;
    }

    const user = await getOverwolfCurrentUser();
    return user?.token ?? null;
  }

  // ---------------------------------------------------------------------------
  private static async fetchInstalledExtensions(
    token: string,
  ): Promise<ExtensionsByMachine | null> {
    try {
      const response = await fetch(kInstalledExtensionsUrl, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        return null;
      }

      const data = (await response.json()) as InstalledExtensionsResponse;
      const grouped = this.groupByMachineName(data);

      this.cache = grouped;
      return grouped;
    } catch {
      return null;
    } finally {
      this.inFlightRequest = null;
    }
  }

  // ---------------------------------------------------------------------------
  private static groupByMachineName(
    response: InstalledExtensionsResponse,
  ): ExtensionsByMachine {
    const result: ExtensionsByMachine = {};

    for (const [extensionId, machineUuids] of Object.entries(response.apps)) {
      for (const machineUuid of machineUuids) {
        const machineName = response.machines[machineUuid];
        if (!machineName) continue;

        if (!result[machineName]) {
          result[machineName] = [];
        }
        result[machineName].push(extensionId);
      }
    }

    return result;
  }
}
